import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, ArrowRight } from 'lucide-react';
import { Card, Input } from '../ui';

export function DuesSplitCalculator() {
  const [duesAmount, setDuesAmount] = useState('100');
  const [hqPercentage, setHqPercentage] = useState('20');
  const [regionPercentage, setRegionPercentage] = useState('30');
  const [transactionFee, setTransactionFee] = useState('0.30');
  const [transactionPercent, setTransactionPercent] = useState('2.9');
  const [includeGatewayFees, setIncludeGatewayFees] = useState(false);

  const total = parseFloat(duesAmount) || 0;
  const hqP = parseFloat(hqPercentage) || 0;
  const regP = parseFloat(regionPercentage) || 0;
  const localP = Math.max(0, 100 - hqP - regP);

  const pctFee = parseFloat(transactionPercent) || 0;
  const fixedFee = parseFloat(transactionFee) || 0;

  let receiptGross = total;
  let computedGatewayFee = 0;
  let netPool = 0;

  if (includeGatewayFees) {
    const rate = pctFee / 100;
    receiptGross = rate < 1 ? (total + fixedFee) / (1 - rate) : total + fixedFee;
    computedGatewayFee = receiptGross - total;
    netPool = total;
  } else {
    computedGatewayFee = (total * pctFee / 100) + fixedFee;
    netPool = Math.max(0, total - computedGatewayFee);
    receiptGross = total;
  }

  const hqPortion = (netPool * hqP / 100);
  const regionPortion = (netPool * regP / 100);
  const localPortion = (netPool * localP / 100);

  return (
    <div className="grid md:grid-cols-2 gap-8">
      <Card className="p-6 space-y-4 border-2 border-black dark:border-white border-gray-150">
        <h3 className="text-lg font-bold flex items-center gap-2 mb-2">
          <Calculator className="text-emerald-500" size={20} /> Inputs
        </h3>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-bold text-gray-400 uppercase font-mono">Dues Ticket Amount ($)</label>
            <Input type="number" value={duesAmount} onChange={(e) => setDuesAmount(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase font-mono">HQ Allocation %</label>
              <Input type="number" value={hqPercentage} onChange={(e) => setHqPercentage(e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase font-mono">Region Allocation %</label>
              <Input type="number" value={regionPercentage} onChange={(e) => setRegionPercentage(e.target.value)} />
            </div>
          </div>
          <div className="pt-2 border-t border-black dark:border-white">
            <span className="text-[10px] font-bold text-blue-600 block mb-2 uppercase font-mono">Gateway Fees (e.g. Stripe)</span>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] text-gray-400 block mb-1">Percentage Fee (%)</label>
                <Input type="number" step="0.1" value={transactionPercent} onChange={(e) => setTransactionPercent(e.target.value)} />
              </div>
              <div>
                <label className="text-[10px] text-gray-400 block mb-1">Fixed Fee ($)</label>
                <Input type="number" step="0.01" value={transactionFee} onChange={(e) => setTransactionFee(e.target.value)} />
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-black dark:border-white flex items-start gap-2">
            <input
              id="includeGatewayFeesCheckbox"
              type="checkbox"
              checked={includeGatewayFees}
              onChange={(e) => setIncludeGatewayFees(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-black dark:border-white text-emerald-600 focus:ring-emerald-500 cursor-pointer"
            />
            <div className="flex flex-col">
              <label htmlFor="includeGatewayFeesCheckbox" className="text-sm font-semibold text-gray-700 dark:text-gray-300 cursor-pointer select-none">
                Add gateway fees to checkout total
              </label>
              <span className="text-xs text-gray-400">
                Member pays the fees upfront so the full dues amount is split among distributions.
              </span>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6 space-y-6 bg-slate-50 dark:bg-slate-900 border-2 border-black dark:border-white">
        <div>
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider font-mono">Earnings Output Breakdown</span>
          <div className="border-b border-black dark:border-white/60 pb-3 mt-4 flex justify-between items-center">
            <span className="text-sm">Receipt Gross:</span>
            <span className="font-bold text-gray-900 dark:text-white font-mono text-base">${receiptGross.toFixed(2)}</span>
          </div>
          <div className="border-b border-black dark:border-white/60 py-3 flex justify-between items-center text-red-650">
            <span className="text-sm">Gateway Processing Fee:</span>
            <span className="font-bold font-mono text-sm">-${computedGatewayFee.toFixed(2)}</span>
          </div>
          <div className="border-b border-black dark:border-white/60 py-3 flex justify-between items-center text-emerald-600">
            <span className="text-sm font-semibold">Net Distributable:</span>
            <span className="font-bold font-mono text-lg">${netPool.toFixed(2)}</span>
          </div>
        </div>

        <div className="space-y-3 pt-2">
          <span className="text-[10px] font-bold text-gray-400 uppercase font-mono tracking-wider block">Split Distribution</span>
          <div className="flex justify-between items-center py-2 px-3 bg-white dark:bg-slate-950 border-2 border-black dark:border-white">
            <span className="text-xs font-semibold">National HQ ({hqP}%):</span>
            <span className="font-bold font-mono text-sm text-gray-800 dark:text-gray-300">${hqPortion.toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center py-2 px-3 bg-white dark:bg-slate-950 border-2 border-black dark:border-white">
            <span className="text-xs font-semibold">Regional Chapter ({regP}%):</span>
            <span className="font-bold font-mono text-sm text-gray-800 dark:text-gray-300">${regionPortion.toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center py-2 px-3 bg-white dark:bg-slate-950 border-2 border-black dark:border-white">
            <span className="text-xs font-semibold">Local Unit (Inherited {localP}%):</span>
            <span className="font-bold font-mono text-sm text-emerald-600">${localPortion.toFixed(2)}</span>
          </div>
        </div>

        <div className="pt-3 border-t-2 border-black dark:border-white">
          <div className="p-4 bg-[#FFD600] text-black border-2 border-black dark:border-white space-y-2">
            <p className="text-xs font-bold leading-snug">
              Want to automate this exact split? Connect Stripe on OrgSets and route {hqP}% to National and {localP}% to Local chapters automatically on every payment.
            </p>
            <Link
              to="/sign-up"
              className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider bg-black text-white hover:bg-[#0044FF] px-3 py-1.5 transition-colors cursor-pointer"
            >
              Create Free Organization <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </Card>
    </div>
  );
}
